import { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import debounce from 'lodash/debounce'

import useFeatureFlag from 'hooks/useFeatureFlag'

import { media } from '../../../styles/media'

import { updateFilters } from '../../../actions/searchActions'

import { FilterPanel, CloseButton, PanelHeader } from './shared'
import CloseIcon from '../../../components/icons/AirbnbClose'

import {
  AdultQuantitySelect,
  ChildQuantitySelect,
  PetQuantitySelect,
} from '../common/QuantitySelect'

import FilterPanelActions from './FilterPanelActions'

const SectionWrapper = styled.div`
  padding: 24px 0;

  ${media.sm`
    padding: 24px 16px;
  `}
`

const CapacityWrapper = styled.div`
  font-size: 20px;
`
type UpdateFnArgs = Pick<
  GQL_SearchFilters,
  'adults' | 'children' | 'pets' | 'petFriendly'
>

type Props = {
  open: boolean
  filters: GQL_SearchFilters
  onClose: () => any
  resultsCount?: number
  update: (_: UpdateFnArgs) => any
  updateDebounceMs?: number
}

export const BasePeopleFilter = ({
  open,
  filters,
  onClose,
  resultsCount,
  update,
  updateDebounceMs = 500,
}: Props) => {
  const petFeesEnabled = useFeatureFlag('pet_fees')

  const [showResultCount, setShowResultCount] = useState(true)
  const [adults, setAdults] = useState(filters?.adults || null)
  const [children, setChildren] = useState(filters?.children || null)
  const [pets, setPets] = useState(filters?.pets || 0)

  const debouncedUpdate = useMemo(
    () =>
      debounce((nextFilters: UpdateFnArgs) => {
        update(nextFilters)
        setShowResultCount(true)
      }, updateDebounceMs),
    [update],
  )

  const updatePeopleFilters = (nextPeople: UpdateFnArgs) => {
    setShowResultCount(false)
    debouncedUpdate({
      ...filters,
      ...nextPeople,
    })
  }

  useEffect(() => {
    setAdults(filters.adults || null)
  }, [filters?.adults])

  useEffect(() => {
    setChildren(filters.children || null)
  }, [filters?.children])

  useEffect(() => {
    setPets(filters?.pets || 0)
  }, [filters?.pets])

  const scrollContainer = useRef<HTMLDivElement>()

  const resetScroll = () => {
    scrollContainer.current.scrollTo(0, 0)
  }

  const handleChangeAdults = (nextAdults: number) => {
    setAdults(nextAdults)
    if (!nextAdults) {
      setChildren(null)
      setPets(0)
      updatePeopleFilters({ adults: nextAdults, children: null, pets: 0 })
      return
    }
    updatePeopleFilters({ adults: nextAdults, children })
  }

  const handleChangeChildren = (nextChildren: number) => {
    const nextAdults = nextChildren > 0 && !adults ? 1 : adults
    setChildren(nextChildren)
    if (nextAdults !== adults) setAdults(nextAdults)
    updatePeopleFilters({ adults: nextAdults, children: nextChildren })
  }

  const handleChangePets = (nextPets: number) => {
    const nextAdults = nextPets > 0 && !adults ? 1 : adults
    setPets(nextPets)
    if (nextAdults !== adults) setAdults(nextAdults)
    updatePeopleFilters({
      adults: nextAdults,
      children,
      pets: nextPets,
      ...(nextPets > 0 ? { petFriendly: true } : {}),
    })
  }

  const handleClosePanel = () => {
    resetScroll()
    onClose()
  }

  const handleApply = () => {
    handleClosePanel()
  }

  const handleResetFilters = () => {
    updatePeopleFilters({ adults: null, children: null, pets: 0 })
    onClose()
  }

  return (
    <FilterPanel className={`${open ? 'show' : ''}`} ref={scrollContainer}>
      <PanelHeader>
        <CloseButton
          data-testid="close-people-filter"
          aria-label="Close people filter"
          onClick={handleClosePanel}
        >
          <CloseIcon />
        </CloseButton>
        <span>Who&apos;s in</span>
      </PanelHeader>

      <SectionWrapper>
        <CapacityWrapper>
          <AdultQuantitySelect value={adults} onChange={handleChangeAdults} />

          <ChildQuantitySelect
            value={children}
            onChange={handleChangeChildren}
          />

          {petFeesEnabled && (
            <PetQuantitySelect value={pets} onChange={handleChangePets} />
          )}
        </CapacityWrapper>
      </SectionWrapper>

      <FilterPanelActions
        onClear={handleResetFilters}
        onView={handleApply}
        resultsCount={!showResultCount ? undefined : resultsCount}
      />
    </FilterPanel>
  )
}

export default connect(undefined, (dispatch) => ({
  update: (filters: GQL_SearchFilters) =>
    dispatch(
      // @ts-ignore -- not typed yet
      updateFilters(filters),
    ),
}))(BasePeopleFilter)
